import qs from 'qs';
import getResponseJson from './getresponsejson';

const CONTRACT_SETTINGS = {
  AUTO_EXPORT: 'AutoApproveExport',
  PURGE_DURING_NEXT_READ: 'PurgeContractDuringNextRead',
  DOC_PROC_AUTOFINALIZE: 'ShouldAutofinalizeOnRead',
  AUTOFINALIZED_ENCOUNTER_DOC_PROC_READ: 'ShouldReadAutofinalized',
  IS_CLIENT_REVIEW_ENABLED: 'IsClientReviewEnabled'
};

export const AUDIT_TYPES = {
  NEW_HCC: 'New Hccs',
  DX: 'Dx',
  HCC: 'Hcc',
  CODER: 'Coder',
  UNVALIDATED_DXES: 'Unvalidated_Dxes',
  CCC_NEW_DX: 'CCC New Dx',
  CCC_UNVALIDATED_DX: 'CCC Unvalidated Dx'
};

export const MANUAL_AUTOFINALIZE_SETTING_NAMES = {
  NO_DXES: 'No Dxes',
  NO_MA_HCCS: 'No MA HCCs',
  NO_NEW_MA_HCCS: 'No New MA HCCs'
};

export const contractManagementService = serviceRoutes => {
  const simpleToggleSetting = (contractId, setting, nextValue) => {
    return fetch(serviceRoutes.toggleSetting, {
      credentials: 'include',
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contractId,
        setting,
        isChecked: nextValue
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  const toggleAutoReread = (contractId, isChecked) => {
      return fetch(serviceRoutes.toggleIsRereadDisabled, {
        credentials: 'include',
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractId,
          isChecked
        })
      })
  };

  const enableClientReview = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.IS_CLIENT_REVIEW_ENABLED,
    true
  );

  const disableClientReview = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.IS_CLIENT_REVIEW_ENABLED,
    false
  );

  /* Auto Export */
  const enableAutoExport = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.AUTO_EXPORT,
    true
  );

  const disableAutoExport = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.AUTO_EXPORT,
    false
  );

  /* Purge During Next Read */
  const enablePurgeDuringNextRead = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.PURGE_DURING_NEXT_READ,
    true
  );

  const disablePurgeDuringNextRead = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.PURGE_DURING_NEXT_READ,
    false
  );

  /* Doc Proc Autofinalize */
  const enableDocProcAutofinalize = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.DOC_PROC_AUTOFINALIZE,
    true
  );

  const disableDocProcAutofinalize = contractId => simpleToggleSetting(
    contractId,
    CONTRACT_SETTINGS.DOC_PROC_AUTOFINALIZE,
    false
  );

  /* Doc Proc Reads Autofinalized Encounters */
  const enableAutofinalizedEncounterDocProcRead = contractId =>
    simpleToggleSetting(
      contractId,
      CONTRACT_SETTINGS.AUTOFINALIZED_ENCOUNTER_DOC_PROC_READ,
      true
    );

  const disableAutofinalizedEncounterDocProcRead = contractId =>
    simpleToggleSetting(
      contractId,
      CONTRACT_SETTINGS.AUTOFINALIZED_ENCOUNTER_DOC_PROC_READ,
      false
    );

  const simpleToggleAudit = (contractId, auditType, isEnabled) => {
    return fetch(serviceRoutes.toggleAuditType, {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contractId,
        auditType,
        isEnabled
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  const simpleAddItemToAudit = (contractId, auditType, itemId) => {
    return fetch(serviceRoutes.addItemToAudit, {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contractId,
        auditType,
        itemId
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  const simpleRemoveItemFromAudit = (contractId, auditType, itemId) => {
    return fetch(serviceRoutes.removeItemFromAudit, {
      credentials: 'include',
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contractId,
        auditType,
        itemId
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  const enableNewHCCAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.NEW_HCC,
    true
  );

  const disableNewHCCAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.NEW_HCC,
    false
  );

  const enableUnvalidatedDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.UNVALIDATED_DXES,
    true
  );

  const disableUnvalidatedDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.UNVALIDATED_DXES,
    false
  );

  const enableCCCNewDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.CCC_NEW_DX,
    true
  );

  const disableCCCNewDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.CCC_NEW_DX,
    false
  );

  const enableCCCUnvalidatedDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.CCC_UNVALIDATED_DX,
    true
  );

  const disableCCCUnvalidatedDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.CCC_UNVALIDATED_DX,
    false
  );

  const enableCoderAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.CODER,
    true
  );

  const disableCoderAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.CODER,
    false
  );

  const placeCoderOnAudit = (contractId, coderId) => {
    return simpleAddItemToAudit(contractId, AUDIT_TYPES.CODER, coderId);
  };

  const removeCoderFromAudit = (contractId, coderId) => {
    return simpleRemoveItemFromAudit(contractId, AUDIT_TYPES.CODER, coderId);
  };

  const enableCCAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.HCC,
    true
  );

  const disableCCAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.HCC,
    false
  );

  const placeCCOnAudit = (contractId, codeCategoryId) => {
    return simpleAddItemToAudit(contractId, AUDIT_TYPES.HCC, codeCategoryId);
  };

  const removeCCFromAudit = (contractId, codeCategoryId) => {
    return simpleRemoveItemFromAudit(contractId, AUDIT_TYPES.HCC, codeCategoryId);
  };

  const enableDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.DX,
    true
  );

  const disableDxAudit = contractId => simpleToggleAudit(
    contractId,
    AUDIT_TYPES.DX,
    false
  );

  const placeDxOnAudit = (contractId, cmsCodeId) => {
    return simpleAddItemToAudit(contractId, AUDIT_TYPES.DX, cmsCodeId);
  };

  const removeDxFromAudit = (contractId, cmsCodeId) => {
    return simpleRemoveItemFromAudit(contractId, AUDIT_TYPES.DX, cmsCodeId);
  };

  const getAffectedEncounters = (contractId, auditType, itemId) => {
    const params = { contractId, auditType, itemId };
    const queryString = qs.stringify(params);
    const url = `${serviceRoutes.getAffectedEncounters}?${queryString}`;

    return fetch(url, {
      credentials: 'include'
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  // auditType and itemId are strings
  const removeAuditReasons = (contractId, auditType, itemId) => {
    return fetch(serviceRoutes.removeAuditReasons, {
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'DELETE',
      credentials: 'include',
      body: JSON.stringify({
        contractId,
        auditType,
        itemId
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  // options is array of [{ name, isChecked }]
  const manualAutofinalize = (contractId, options) => {
    return fetch(serviceRoutes.manualAutofinalize, {
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        contractId,
        options
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

const changePrioritySetting = contractSetting => {
  return fetch(serviceRoutes.updateNextRecordStrategySettingController, {
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(contractSetting)
  })
  .then(getResponseJson)
  .then(responseJson => {
    const { result } = responseJson;
    return Promise.resolve(result);
  });
}

  const purgeEncounters = contractId => {
    console.log('inside service call bottom');
    return fetch(serviceRoutes.purgeEncounters, {
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify({
        'id': contractId
      })
    })
    .then(getResponseJson)
    .then(responseJson => {
      const { result } = responseJson;
      return Promise.resolve(result);
    });
  };

  const enableAutoReread = contractId => toggleAutoReread(
    contractId,
    false
  );

  const disableAutoReread = contractId => toggleAutoReread(
    contractId,
    true
  );

  return {
    removeAuditReasons,
    getAffectedEncounters,
    enableCCAudit,
    disableCCAudit,
    enableDxAudit,
    disableDxAudit,
    enableCoderAudit,
    disableCoderAudit,
    placeCCOnAudit,
    removeCCFromAudit,
    placeDxOnAudit,
    removeDxFromAudit,
    placeCoderOnAudit,
    removeCoderFromAudit,
    enableNewHCCAudit,
    disableNewHCCAudit,
    enableAutoExport,
    disableAutoExport,
    enablePurgeDuringNextRead,
    disablePurgeDuringNextRead,
    enableDocProcAutofinalize,
    disableDocProcAutofinalize,
    enableUnvalidatedDxAudit,
    disableUnvalidatedDxAudit,
    enableCCCNewDxAudit,
    disableCCCNewDxAudit,
    enableCCCUnvalidatedDxAudit,
    disableCCCUnvalidatedDxAudit,
    enableAutofinalizedEncounterDocProcRead,
    disableAutofinalizedEncounterDocProcRead,
    manualAutofinalize,
    enableAutoReread,
    disableAutoReread,
    purgeEncounters,
    serviceRoutes,
    changePrioritySetting,
    enableClientReview,
    disableClientReview
  };
};

export default contractManagementService;
